import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SUBFEATURE_TO_MODULE,
  type FeatureFlags,
  type FeatureFlagsPatch,
  type FeatureKey,
  type ModuleFlags,
} from '@condo/contracts';

export interface FlagDiffEntry {
  key: FeatureKey;
  before: boolean;
  after: boolean;
}

export interface FlagForm {
  staged: FeatureFlags;
  diff: FlagDiffEntry[];
  isDirty: boolean;
  stage: (key: FeatureKey, value: boolean) => void;
  reset: () => void;
  isParentOff: (key: FeatureKey) => boolean;
  buildPatch: () => FeatureFlagsPatch;
}

function setFlag(doc: FeatureFlags, key: FeatureKey, value: boolean): FeatureFlags {
  const [group, name] = key.split('.') as [keyof FeatureFlags, string];
  const groupValue = { ...(doc[group] as Record<string, boolean>), [name]: value };
  return { ...doc, [group]: groupValue };
}

export function useFlagForm(initial: FeatureFlags): FlagForm {
  const [staged, setStaged] = useState<FeatureFlags>(initial);

  // Keep a stable ref to the latest initial so reset() never goes stale.
  const initialRef = useRef(initial);
  initialRef.current = initial;

  // Reset when the initial doc changes (new tenant selected, post-save refresh).
  // We key on tenantId + updatedAt rather than the object reference to avoid
  // spurious resets when the parent re-renders with a structurally-equal value
  // (e.g. renderHook recreating the argument object on every render).
  useEffect(() => {
    setStaged(initialRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.tenantId, initial.updatedAt]);

  const stage = useCallback((key: FeatureKey, value: boolean) => {
    setStaged((prev) => setFlag(prev, key, value));
  }, []);

  const reset = useCallback(() => {
    setStaged(initialRef.current);
  }, []);

  const diff = useMemo<FlagDiffEntry[]>(() => {
    const entries: FlagDiffEntry[] = [];
    const groups: (keyof FeatureFlags)[] = ['modules', 'ia', 'integrations', 'red'];
    const snap = initialRef.current;
    for (const group of groups) {
      const stagedGroup = staged[group] as Record<string, boolean>;
      const initialGroup = snap[group] as Record<string, boolean>;
      for (const name of Object.keys(stagedGroup)) {
        const before = Boolean(initialGroup[name]);
        const after = Boolean(stagedGroup[name]);
        if (before !== after) {
          entries.push({ key: `${group}.${name}` as FeatureKey, before, after });
        }
      }
    }
    return entries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staged, initial.tenantId, initial.updatedAt]);

  const isParentOff = useCallback(
    (key: FeatureKey): boolean => {
      const parent = SUBFEATURE_TO_MODULE[key];
      if (!parent) return false;
      return staged.modules[parent as keyof ModuleFlags] === false;
    },
    [staged],
  );

  const buildPatch = useCallback((): FeatureFlagsPatch => {
    const patch: FeatureFlagsPatch = {};
    for (const entry of diff) {
      const [group, name] = entry.key.split('.') as [keyof FeatureFlagsPatch, string];
      if (!patch[group]) {
        (patch as Record<string, Record<string, boolean>>)[group] = {};
      }
      (patch[group] as Record<string, boolean>)[name] = entry.after;
    }
    return patch;
  }, [diff]);

  return {
    staged,
    diff,
    isDirty: diff.length > 0,
    stage,
    reset,
    isParentOff,
    buildPatch,
  };
}
